import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Descriptions, Card, Tabs, Button, Space, Timeline, Spin, Tag, Modal, Form, Input, Select } from 'antd';
import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/common/PageHeader';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ErrorResult } from '../../components/common/ErrorResult';
import { confirmAction } from '../../components/common/ConfirmAction';
import { useUserDetail, useUserActivity, useDeactivateUser, useReactivateUser, useResetPassword, useVerifyEmail, useDeleteUser, useSendEmail } from '../../hooks/useUsers';
import { useTemplatesList, useTemplateDetail } from '../../hooks/useSupport';
import { usePermissions } from '../../hooks/usePermissions';
import dayjs from 'dayjs';

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, isError, refetch } = useUserDetail(id!);
  const { data: activity } = useUserActivity(id!);
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const resetPassword = useResetPassword();
  const verifyEmail = useVerifyEmail();
  const deleteUser = useDeleteUser();
  const sendEmail = useSendEmail();
  const { canDeleteUsers } = usePermissions();

  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [emailForm] = Form.useForm();

  const { data: templates } = useTemplatesList();
  const { data: selectedTemplate } = useTemplateDetail(selectedTemplateId ?? '');

  if (isLoading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (isError || !user) return <ErrorResult onRetry={refetch} />;

  const handleTemplateSelect = (templateId: string | null) => {
    setSelectedTemplateId(templateId || null);
    if (!templateId) {
      emailForm.setFieldsValue({ subject: '', body: '' });
      return;
    }
    // Template detail will populate via effect below
  };

  // When selectedTemplate loads, populate form
  if (selectedTemplate && selectedTemplateId) {
    const currentSubject = emailForm.getFieldValue('subject');
    if (!currentSubject || currentSubject !== selectedTemplate.subject) {
      emailForm.setFieldsValue({
        subject: selectedTemplate.subject,
        body: selectedTemplate.body_html,
      });
    }
  }

  const handleSendEmail = (values: { subject: string; body: string; template_id?: string }) => {
    sendEmail.mutate(
      { id: id!, data: { subject: values.subject, body: values.body, template_id: selectedTemplateId || undefined } },
      {
        onSuccess: () => {
          setEmailModalOpen(false);
          emailForm.resetFields();
          setSelectedTemplateId(null);
        },
      },
    );
  };

  const openEmailModal = () => {
    emailForm.resetFields();
    setSelectedTemplateId(null);
    setEmailModalOpen(true);
  };

  return (
    <>
      <PageHeader
        title={user.full_name || user.email}
        subtitle={user.email}
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/users')}>Back</Button>
            <Button icon={<MailOutlined />} onClick={openEmailModal}>Send Email</Button>
            <Button onClick={() => resetPassword.mutate(id!)}>Reset Password</Button>
            {!user.email_verified && <Button onClick={() => verifyEmail.mutate(id!)}>Verify Email</Button>}
            {user.is_active
              ? <Button danger onClick={() => confirmAction({ title: 'Deactivate User', content: `Deactivate ${user.email}?`, onConfirm: () => deactivate.mutate(id!), danger: true })}>Deactivate</Button>
              : <Button type="primary" onClick={() => reactivate.mutate(id!)}>Reactivate</Button>}
            {canDeleteUsers && <Button danger type="primary" onClick={() => confirmAction({ title: 'Delete User', content: `Permanently delete ${user.email}? This cannot be undone.`, onConfirm: () => { deleteUser.mutate(id!); navigate('/users'); }, danger: true })}>Delete</Button>}
          </Space>
        }
      />
      <Tabs items={[
        { key: 'profile', label: 'Profile', children: (
          <Card>
            <Descriptions column={2}>
              <Descriptions.Item label="Status"><StatusBadge status={user.is_active ? 'active' : 'inactive'} /></Descriptions.Item>
              <Descriptions.Item label="Email Verified">{user.email_verified ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>}</Descriptions.Item>
              <Descriptions.Item label="Auth Provider">{user.auth_provider}</Descriptions.Item>
              <Descriptions.Item label="Plan">{user.plan_tier || '-'}</Descriptions.Item>
              <Descriptions.Item label="Organization">{user.organization_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Invoices Processed">{user.invoices_processed}</Descriptions.Item>
              <Descriptions.Item label="Timezone">{user.timezone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Locale">{user.locale || '-'}</Descriptions.Item>
              <Descriptions.Item label="Created">{dayjs(user.created_at).format('MMM D, YYYY HH:mm')}</Descriptions.Item>
              <Descriptions.Item label="Last Login">{user.last_login_at ? dayjs(user.last_login_at).format('MMM D, YYYY HH:mm') : 'Never'}</Descriptions.Item>
              <Descriptions.Item label="Admin">{user.is_admin ? <Tag color="purple">{user.admin_role}</Tag> : 'No'}</Descriptions.Item>
            </Descriptions>
          </Card>
        )},
        { key: 'activity', label: 'Activity', children: (
          <Card>
            <Timeline items={activity?.map((a) => ({ children: <><strong>{a.action}</strong> on {a.resource_type} <br /><small>{dayjs(a.created_at).format('MMM D, YYYY HH:mm')} {a.ip_address && `from ${a.ip_address}`}</small></> })) || []} />
          </Card>
        )},
      ]} />

      {/* Send Email Modal */}
      <Modal
        title={`Send Email to ${user.email}`}
        open={emailModalOpen}
        onCancel={() => setEmailModalOpen(false)}
        footer={null}
        width={700}
      >
        <Form form={emailForm} layout="vertical" onFinish={handleSendEmail}>
          <Form.Item label="Template (optional)">
            <Select
              allowClear
              placeholder="Select a template or compose from scratch"
              value={selectedTemplateId}
              onChange={handleTemplateSelect}
              options={(templates || [])
                .filter((t) => t.is_active)
                .map((t) => ({ label: `${t.name} (${t.category || 'general'})`, value: t.id }))}
            />
          </Form.Item>

          <Form.Item name="subject" label="Subject" rules={[{ required: true, message: 'Subject is required' }]}>
            <Input placeholder="Email subject line" />
          </Form.Item>

          <Form.Item name="body" label="Body (HTML)" rules={[{ required: true, message: 'Body is required' }]}>
            <Input.TextArea rows={10} style={{ fontFamily: 'monospace', fontSize: 12 }} placeholder="<p>Hello {{name}},</p>" />
          </Form.Item>

          <Space>
            <Button type="primary" htmlType="submit" icon={<MailOutlined />} loading={sendEmail.isPending}>
              Send Email
            </Button>
            <Button onClick={() => setEmailModalOpen(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
